<template>
  <div
    class="base-tooltip"
    :style="styles"
    @click.stop
  >
    <Hover
      v-if="triggerHover"
      v-model="visibility"
    >
      <div ref="content">
        <slot name="content" />
      </div>
    </Hover>
    <div
      v-if="triggerClick"
      @click="visibility = !visibility"
    >
      <div ref="content">
        <slot name="content" />
      </div>
    </div>
    <div
      v-if="slotHasContent"
      ref="tooltip"
      :style="styles"
      :class="{
        'base-tooltip--container': true,
        [internalAlignment]: internalAlignment,
        show: visibility,
      }"
    >
      <svg
        v-if="['right', 'bottom'].includes(internalAlignment)"
        xmlns="http://www.w3.org/2000/svg"
        :class="`triangle-${internalAlignment}`"
      >
        <polygon
          v-if="internalAlignment === 'bottom'"
          points="0,0 20,0 10,10"
        />
        <polygon
          v-if="internalAlignment === 'right'"
          points="0,0 0,20 10,10"
        />
      </svg>
      <div
        :class="{
          'base-tooltip--tooltip': true,
        }"
      >
        <span
          v-if="title"
          class="p-0"
        >{{ title }}</span>
        <slot name="tooltip" />
      </div>
      <svg
        v-if="['left', 'top'].includes(internalAlignment)"
        xmlns="http://www.w3.org/2000/svg"
        :class="`triangle-${internalAlignment}`"
      >
        <polygon
          v-if="internalAlignment === 'top'"
          points="0,0 20,0 10,10"
        />
        <polygon
          v-if="internalAlignment === 'left'"
          points="0,0 0,20 10,10"
        />
      </svg>
    </div>
  </div>
</template>

<script>
export default {
  name: 'BaseTooltip',
  model: {
    prop: 'visibility',
    event: 'update',
  },
  props: {
    title: {
      type: String,
      default: '',
    },
    alignment: {
      type: String,
      default: 'bottom',
      validator(val) {
        return ['top', 'bottom', 'left', 'right'].indexOf(val) !== -1;
      },
    },
    trigger: {
      type: String,
      default: 'hover',
      validator(val) {
        return ['hover', 'click'].indexOf(val) !== -1;
      },
    },
    parentElement: {
      type: String,
      default: 'body',
    },
    maxWidth: {
      type: String,
      default: '50vw',
    },
  },
  data() {
    return {
      isMounted: false,
      contentRef: null,
      tooltipRef: null,
      visibility: false,
      slotHasContent: false,
      tooltipWidth: 0,
      tooltipHeight: 0,
      align: this.alignment,
    };
  },
  computed: {
    styles: {
      set({ tooltipWidth, tooltipHeight }) {
        this.tooltipWidth = tooltipWidth;
        this.tooltipHeight = tooltipHeight;
      },
      get() {
        return {
          '--content-width': this.isMounted ? `${this.contentRef.scrollWidth}px` : '',
          '--content-height': this.isMounted ? `${this.contentRef.scrollHeight}px` : '',
          '--tooltip-width': this.isMounted ? `${this.tooltipWidth}px` : '',
          '--tooltip-height': this.isMounted ? `${this.tooltipHeight}px` : '',
          '--max-width': this.isMounted ? `${this.maxWidth}` : '',
        };
      },
    },
    internalAlignment: {
      get() {
        return this.align;
      },
      set(val) {
        this.align = val;
      },
    },
    triggerClick() {
      return this.trigger === 'click';
    },
    triggerHover() {
      return this.trigger === 'hover';
    },
  },
  watch: {
    visibility(val) {
      this.$nextTick(() => {
        this.handleScroll();
        this.intersection();
        if (this.$refs.tooltip && this.$refs.tooltip.scrollWidth) {
          this.styles = {
            tooltipWidth: this.$refs.tooltip.getBoundingClientRect().width,
            tooltipHeight: this.$refs.tooltip.getBoundingClientRect().height,
          };
          document.querySelector(this.parentElement).appendChild(this.$refs.tooltip);
        }
        if (val) {
          window.addEventListener('scroll', this.handleScroll);
        } else {
          window.removeEventListener('scroll', this.handleScroll);
        }
      });
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.tooltipRef = this.$refs.tooltip;
      this.contentRef = this.$refs.content;
      const hasTooltipSlot = this.$slots.tooltip ? !!this.$slots.tooltip.length : false;
      this.slotHasContent = !!this.title || hasTooltipSlot;
      this.isMounted = true;
    });
  },
  beforeDestroy() {
    // Close tooltip and clean up when component is destroyed
    this.visibility = false;
    window.removeEventListener('scroll', this.handleScroll);

    // Remove tooltip from DOM if it was appended to parent
    if (this.$refs.tooltip && this.$refs.tooltip.parentNode) {
      this.$refs.tooltip.parentNode.removeChild(this.$refs.tooltip);
    }
  },
  methods: {
    handleScroll() {
      const getPos = (el) => el.getBoundingClientRect();
      let top = 0;
      let left = 0;
      const {
        top: contentTop, left: contentLeft, height, width,
      } = getPos(this.contentRef);

      switch (this.internalAlignment) {
        case 'top':
          top = contentTop - getPos(this.$refs.tooltip).height - 5;
          left = contentLeft - getPos(this.$refs.tooltip).width / 2 + width / 2;
          break;
        case 'bottom':
          top = contentTop + height + 5;
          left = contentLeft - getPos(this.$refs.tooltip).width / 2 + width / 2;
          break;
        case 'left':
          top = contentTop - getPos(this.$refs.tooltip).height / 2 + height / 2;
          left = contentLeft - getPos(this.$refs.tooltip).width - 5;
          break;
        case 'right':
          top = contentTop - getPos(this.$refs.tooltip).height / 2 + height / 2;
          left = contentLeft + width + 5;
          break;
        default:
          top = contentTop - getPos(this.$refs.tooltip).height - 5;
          left = contentLeft - getPos(this.$refs.tooltip).width / 2 + width / 2;
          break;
      }

      this.$refs.tooltip.style.top = `${top}px`;
      this.$refs.tooltip.style.left = `${left}px`;
    },
    intersection() {
      this.$nextTick(() => {
        if (
          window.innerHeight === 0 || !this.$refs.tooltip
        ) { /* empty */ } else if (
          this.$refs.tooltip.getBoundingClientRect().bottom >= window.innerHeight
        ) {
          this.internalAlignment = 'top';
        } else if (this.$refs.tooltip.getBoundingClientRect().top <= 0) {
          this.internalAlignment = 'bottom';
        } else if (this.$refs.tooltip.getBoundingClientRect().left <= 0) {
          this.internalAlignment = 'right';
        } else if (this.$refs.tooltip.getBoundingClientRect().right >= window.innerWidth) {
          this.internalAlignment = 'left';
        }

        this.handleScroll();
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.base-tooltip {
    position: relative;
    cursor: pointer;
    pointer-events: all;

    &--container {
        opacity: 0;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        pointer-events: none;
        position: fixed;
        max-width: var(--max-width);
        height: fit-content;
        font-size: 1rem;
        color: #000;
        z-index: 5500;
        filter: drop-shadow(0px 1px 3px #0005);

        &.show {
            opacity: 1;
            pointer-events: all;
        }

        &.left,
        &.right {
          flex-direction: row;
        }

        .triangle-bottom {
          margin: 0 auto -1px;
          width: 20px;
          height: 11px;
          transform: rotate(180deg);

          polygon {
            fill: #fff;
            stroke: #dbdbdb;
          }
        }

        .triangle-top {
          margin: -1px auto 0;
          width: 20px;
          height: 11px;

          polygon {
            fill: #fff;
            stroke: #dbdbdb;
          }
        }

        .triangle-right {
          margin: 0 -1px 0 auto;
          width: 11px;
          height: 20px;
          transform: rotate(180deg);

          polygon {
            fill: #fff;
            stroke: #dbdbdb;
          }
        }

        .triangle-left {
          margin: 0 auto 0 -1px;
          width: 11px;
          height: 20px;

          polygon {
            fill: #fff;
            stroke: #dbdbdb;
          }
        }
    }

    &--tooltip {
        position: relative;
        padding: 1rem;
        border-radius: 20px;
        background-color: #fff;
        flex: 1;

        span {
            display: block;
            padding-bottom: 1rem;
        }

        &.top {
            top: unset;
            bottom: calc(var(--content-height) + 15px);
        }

        &.left {
            left: unset;
            top: calc((var(--tooltip-height) / -2) - calc(var(--content-height) / -2));
            right: calc(var(--content-width) + 15px);
        }

        &.right {
            top: calc((var(--tooltip-height) / -2) - calc(var(--content-height) / -2));
            left: calc(var(--content-width) + 15px);
        }
    }
}
</style>
